"use client";

import React, { useEffect } from "react";
import * as z from "zod";

import { PhoneInputFormField } from "@/components/form/phone-input-form-field";
import { TextInputFormField } from "@/components/form/text-input-form-field";
import { TextAreaFormField } from "@/components/form/textarea-form-field";
import { Form } from "@/components/shadcn/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/shadcn/alert";
import { Button } from "@/components/shadcn/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/shadcn/card";
import { Spinner } from "@/components/shadcn/spinner";
import { fetcher } from "@/lib/fetcher";
import { validatePhoneNumber } from "@/lib/utils";
import type { User } from "@prisma/client";
import DOMPurify from "isomorphic-dompurify";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

interface ProfileSaveErrorResponse {
    error?: ProfileSaveIssue[] | string;
}

interface ProfileSaveIssue {
    code?: string;
    message: string;
    path?: Array<string | number>;
}

class ProfileSaveError extends Error {
    constructor(readonly responseData?: ProfileSaveErrorResponse) {
        super("Profile update failed");
    }
}

function SettingsCard({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Card>
            <CardHeader className="sr-only">
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

export default function ProfileSettingsForm(): React.ReactNode {
    const t = useTranslations("account");
    const tCommon = useTranslations("common");

    // The API answers with English messages; map the known ones onto the
    // catalog and pass anything unrecognised straight through.
    const translateProfileSaveError = (message: string) => {
        const errorMap: { [key: string]: string } = {
            "First name is required": t("firstNameRequired"),
            "Last name is required": t("lastNameRequired"),
            "Please enter a valid phone number": t("phoneInvalid"),
            "Internal server error": t("serverError"),
            Unauthorized: t("unauthorized"),
        };

        return errorMap[message] || message;
    };

    const {
        data: user,
        error,
        isLoading,
        isValidating,
        mutate: mutateUserDetails,
    } = useSWR<User | null, Error>("/api/user/details", fetcher);

    const sanitizer = DOMPurify.sanitize;

    const formSchema = z
        .object({
            name: z.string().min(1, t("firstNameRequired")),
            surname: z.string().min(1, t("lastNameRequired")),
            eloRating: z
                .string()
                .optional()
                .transform((val) => (val ? parseInt(val, 10) : null))
                .pipe(
                    z
                        .number()
                        .min(0, t("eloMin"))
                        .max(3000, t("eloMax"))
                        .nullable(),
                ),
            about: z.string().optional(),
            phone: z
                .string()
                .transform((val) => val)
                .pipe(
                    z
                        .string()
                        .refine(
                            (val) =>
                                validatePhoneNumber(val.replace(/\s+/g, "")),
                            {
                                message: t("phoneInvalid"),
                            },
                        ),
                ),
        })
        .transform((v) => {
            return {
                name: sanitizer(v.name || ""),
                surname: sanitizer(v.surname || ""),
                eloRating: sanitizer(v.eloRating?.toString() || ""),
                about: sanitizer(v.about || ""),
                phone: sanitizer(v.phone || ""),
            };
        });

    type ValidationSchema = z.infer<typeof formSchema>;

    const router = useRouter();

    const saveProfile = async (
        url: string,
        { arg }: { arg: ValidationSchema },
    ) => {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(arg),
        });

        if (!response.ok) {
            const responseData = (await response
                .json()
                .catch(() => undefined)) as ProfileSaveErrorResponse;

            throw new ProfileSaveError(responseData);
        }

        return response.json() as Promise<User>;
    };

    const { trigger: saveProfileTrigger, isMutating: isSavingProfile } =
        useSWRMutation("/api/user/profile-save", saveProfile);

    const defaultValues = {
        name: "",
        surname: "",
        eloRating: "",
        about: "",
        phone: "",
    };

    const form = useForm<ValidationSchema>({
        resolver: zodResolver(formSchema),
        defaultValues,
    });

    useEffect(() => {
        if (user) {
            form.reset({
                name: user.name || "",
                surname: user.surname || "",
                eloRating: user.eloRating?.toString() || "",
                phone: user.phone || "",
                about: user.aboutMe || "",
            });
        }
    }, [user, form]);

    const onSubmit = async (values: ValidationSchema) => {
        try {
            const updatedUser = await saveProfileTrigger(values);

            await mutateUserDetails(updatedUser, {
                revalidate: false,
            });

            toast.success(t("profileUpdated"), {
                description: t("profileUpdatedDesc"),
            });
            router.push("/kullanici/ayarlar");
        } catch (error) {
            if (error instanceof ProfileSaveError) {
                const errorArray = error.responseData?.error || [];

                if (Array.isArray(errorArray) && errorArray.length > 0) {
                    const errorMessages = errorArray
                        .map((err) => translateProfileSaveError(err.message))
                        .join(", ");

                    // Set form errors with translated messages
                    errorArray.forEach((err) => {
                        if (err.path && err.path.length > 0) {
                            form.setError(
                                err.path[0] as keyof ValidationSchema,
                                {
                                    type: err.code,
                                    message: translateProfileSaveError(
                                        err.message,
                                    ),
                                },
                            );
                        }
                    });

                    toast.error(t("validationError"), {
                        description: errorMessages,
                    });
                    return;
                }

                toast.error(t("profileUpdateFailed"), {
                    description: Array.isArray(errorArray)
                        ? t("profileUpdateFailedDesc")
                        : translateProfileSaveError(errorArray),
                });
            } else {
                toast.error(t("profileUpdateFailed"), {
                    description: t("profileUpdateUnexpected"),
                });
            }
            console.error("Profile update error:", error);
        }
    };

    return (
        <>
            {isLoading ? (
                <SettingsCard title={t("profileLoading")}>
                    <div className="flex h-80 w-full items-center justify-center">
                        <Spinner />
                    </div>
                </SettingsCard>
            ) : error && !user ? (
                <SettingsCard title={t("profileLoadFailed")}>
                    <Alert variant="destructive">
                        <AlertTitle>{t("profileLoadFailed")}</AlertTitle>
                        <AlertDescription>
                            {t("profileLoadRetry")}
                        </AlertDescription>
                    </Alert>
                    <div className="mt-6 flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isValidating}
                            onClick={() => void mutateUserDetails()}
                        >
                            {tCommon("retry")}
                        </Button>
                    </div>
                </SettingsCard>
            ) : !user ? (
                <SettingsCard title={t("profileNotFound")}>
                    <Alert>
                        <AlertTitle>{t("profileNotFound")}</AlertTitle>
                        <AlertDescription>
                            {t("profileSessionRetry")}
                        </AlertDescription>
                    </Alert>
                    <div className="mt-6 flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isValidating}
                            onClick={() => void mutateUserDetails()}
                        >
                            {tCommon("retry")}
                        </Button>
                    </div>
                </SettingsCard>
            ) : (
                <SettingsCard title={t("profileInfo")}>
                    <Form {...form}>
                        <form
                            className="flex flex-col gap-6"
                            onSubmit={form.handleSubmit(onSubmit)}
                        >
                            <div className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
                                <TextInputFormField
                                    control={form.control}
                                    name="name"
                                    label={t("firstName")}
                                    placeholder={t("firstNamePlaceholder")}
                                    required
                                />
                                <TextInputFormField
                                    control={form.control}
                                    name="surname"
                                    label={t("lastName")}
                                    placeholder={t("lastNamePlaceholder")}
                                    required
                                />
                                <PhoneInputFormField
                                    control={form.control}
                                    name="phone"
                                    label={t("phone")}
                                    placeholder="(555) 00 00"
                                    required
                                />
                                <TextInputFormField
                                    control={form.control}
                                    name="eloRating"
                                    label={t("eloRating")}
                                    placeholder="1200"
                                    required
                                />
                                <div className="hidden">
                                    <TextAreaFormField
                                        control={form.control}
                                        name="about"
                                        label={t("aboutMe")}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    type="submit"
                                    disabled={isSavingProfile}
                                >
                                    {isSavingProfile
                                        ? tCommon("saving")
                                        : t("saveChanges")}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </SettingsCard>
            )}
        </>
    );
}
